// Task #1305 - sezione UI per i service_key di platform_model_config
// NON coperti dal layout principale di ServiceRoutingTab.
// Lista dinamica da GET /master/platform-models/, esclude quelli gia' renderizzati sopra.
//
// Task #1446 — provider-select unificato (ServiceProviderSelect condiviso col
// layout principale): stessi dot/badge/whitelist. workflow_builder escluso da
// qui (governato dalla card dedicata WorkflowBuilderAIConfigCard).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ServiceProviderSelect, type AvailableProviderEntry, type ProviderAvailability } from '@easyflow/ui-ai-routing';
import { isServicePlatformScoped } from '@easyflow/shared';
import { apiClient, extractStructuredApiError, formatApiErrorMessage } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import ServiceModelPicker from './TenantServiceModelPicker';
import FunctionHelp from './FunctionHelp';

/**
 * Ogni servizio di questa sezione è configurato per il tenant
 * (`/ai-tools/service-models`): le scelte non toccano gli altri tenant. Solo
 * l'embedding non è instradabile, perché lo decide la scelta embedder.
 */
const PLATFORM_SCOPED_READONLY_REASON =
  'Deciso dalla scelta embedder del tenant.';

// Nomi "linguaggio umano" + tooltip esplicativo (cosa fa + dove si usa) per i
// service_key piattaforma renderizzati in questa sezione. Copy UI locale,
// coerente con SERVICE_ITEMS / STT_SUBROWS di ServiceRoutingTab. Le righe
// "(configurazione)" sono holder di extra_config: provider/modello sono
// ininfluenti, conta la configurazione.
interface ServiceMeta {
  label: string;
  tooltip: string;
  usedIn: string;
}
const SERVICE_META: Record<string, ServiceMeta> = {
  agent_chat: {
    label: 'Agenti AI — modello predefinito',
    tooltip:
      "Provider e modello con cui nascono i nuovi agenti (chatbot, Personal Assistant) quando non ne scegli uno nel modale. Gli agenti esistenti mantengono il proprio modello.",
    usedIn: "Creazione agenti chatbot e Personal Assistant.",
  },
  file_analysis: {
    label: 'Analisi file (Vision)',
    tooltip:
      "Analizza con un modello vision i file caricati (immagini, PDF, screenshot) per descriverne e interpretarne il contenuto.",
    usedIn: "File Manager — analisi AI di un file.",
  },
  file_summary: {
    label: 'Riassunto file (AI)',
    tooltip:
      "Genera un riassunto testuale automatico del contenuto di un file e lo salva accanto al file per la lettura e l'indicizzazione.",
    usedIn: "File Manager — pulsante “Riassunto AI” del file.",
  },
  landing_fallback: {
    label: 'Landing Builder — modello di default (fallback)',
    tooltip:
      "Default di tenant per la generazione delle pagine: il backend lo usa SOLO quando la singola landing non ha un modello scelto nel modale “Impostazioni Site Builder”. Il modello impostato nel modale fa override per-pagina e ha la precedenza.",
    usedIn: "Landing Page Builder — fallback quando il modale non specifica un modello.",
  },
  landing_vision: {
    label: 'Landing Builder — analisi immagini (Vision, fallback)',
    tooltip:
      "Default di tenant per il modello vision (lettura di immagini/screenshot di riferimento): il backend lo usa SOLO quando la singola landing non ha un modello vision scelto nel modale “Impostazioni Site Builder”. Il modello vision impostato nel modale fa override per-pagina e ha la precedenza.",
    usedIn: "Landing Page Builder — fallback quando il modale non specifica un modello vision.",
  },
  rag_eval: {
    label: 'Valutazione qualita\u0300 risposte RAG',
    tooltip:
      "Modello giudice che valuta a posteriori la qualita\u0300 delle risposte della Knowledge Base (pertinenza, fedelta\u0300 alle fonti). Non interviene sulle risposte date agli utenti.",
    usedIn: "RAG Analytics — valutazione delle risposte.",
  },
  rag_quality_features: {
    label: 'Flag qualita\u0300 RAG (configurazione)',
    tooltip:
      "Non e\u0300 un modello attivo: e\u0300 il contenitore dei flag di qualita\u0300 della pipeline RAG (attiva/disattiva ottimizzazioni di retrieval). Qui provider e modello sono ininfluenti, conta la configurazione.",
    usedIn: "Pipeline RAG (impostazione globale).",
  },
  rag_query_expansion: {
    label: 'Espansione query (configurazione)',
    tooltip:
      "Non e\u0300 un modello attivo: e\u0300 il contenitore di configurazione dell'espansione query (parametri on/off). Il modello che genera effettivamente la query espansa e\u0300 «Espansione query / HyDE — modello».",
    usedIn: "Pipeline RAG (impostazione dell'espansione query).",
  },
  rag_query_expansion_model: {
    label: 'Espansione query / HyDE — modello',
    tooltip:
      "Modello che genera la query espansa con tecnica HyDE (produce una risposta ipotetica usata per migliorare il retrieval). Gira prima del recupero, sulle query che attivano l'espansione.",
    usedIn: "Pipeline RAG (generazione della query espansa).",
  },
  rag_topk_strategy: {
    label: 'Top-K adattivo (configurazione)',
    tooltip:
      "Non e\u0300 un modello attivo: e\u0300 il contenitore di configurazione della strategia Top-K adattiva (quanti chunk recuperare in base alla query). Conta la configurazione, non provider/modello.",
    usedIn: "Pipeline RAG (selezione numero di chunk).",
  },
  stt_workspace: {
    label: 'Trascrizione microfono Assistant (STT)',
    tooltip:
      "Trascrizione vocale (Speech-to-Text) del microfono nel workspace del Personal Assistant: converte in testo cio\u0300 che detti.",
    usedIn: "Personal Assistant — dettatura vocale nel workspace.",
  },
};

export interface OtherPlatformServicesSectionProps {
  excludeServiceKeys: ReadonlyArray<string>;
}

interface PlatformModelRow {
  service_key: string;
  current_model: string;
  provider: string;
}

export default function OtherPlatformServicesSection({
  excludeServiceKeys,
}: OtherPlatformServicesSectionProps) {
  const { toast } = useToast();
  const [rows, setRows] = useState<PlatformModelRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Unica sorgente per le select provider: `/ai-tools/available-providers`
  // (capability filter + endpoint custom tenant). In caricamento la select è
  // disabilitata; su errore mostra motivo + retry. Nessun ripiego client-side.
  const [providerAvailabilityByKey, setProviderAvailabilityByKey] = useState<Record<string, ProviderAvailability>>({});

  const fetchAvailableProvidersFor = useCallback(async (serviceKey: string) => {
    const fail = (message: string): void => {
      setProviderAvailabilityByKey((prev) => ({
        ...prev,
        [serviceKey]: { status: 'error', message, onRetry: () => { void fetchAvailableProvidersFor(serviceKey); } },
      }));
    };
    setProviderAvailabilityByKey((prev) => (prev[serviceKey]?.status === 'ready' ? prev : { ...prev, [serviceKey]: { status: 'loading' } }));
    try {
      // apiClient restituisce il body `{ success, data: { providers } }`.
      const res = await apiClient.get<{ providers?: AvailableProviderEntry[] }>(
        `/ai-tools/available-providers?service_key=${encodeURIComponent(serviceKey)}`,
      );
      const providers = res.data?.providers;
      if (!Array.isArray(providers)) {
        fail('Risposta del server senza elenco provider.');
        return;
      }
      setProviderAvailabilityByKey((prev) => ({ ...prev, [serviceKey]: { status: 'ready', providers } }));
    } catch (err) {
      fail(formatApiErrorMessage(err, 'Elenco provider non caricato'));
    }
  }, []);

  const excludeSet = useMemo(() => new Set(excludeServiceKeys), [excludeServiceKeys]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Valori effettivi del tenant (riga del tenant sopra il default di piattaforma).
      const res = await apiClient.get<PlatformModelRow[]>('/ai-tools/service-models');
      const safe = Array.isArray(res.data) ? res.data : [];
      setRows(safe.filter((r) => !excludeSet.has(r.service_key)));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [excludeSet]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Popola `providerAvailabilityByKey` per ogni riga visibile.
  // Re-fetch quando cambia la lista (es. nuovi service_key sbloccati).
  useEffect(() => {
    if (rows.length === 0) return;
    void Promise.all(rows.map((r) => fetchAvailableProvidersFor(r.service_key)));
  }, [rows, fetchAvailableProvidersFor]);

  const handleProviderChange = useCallback(async (serviceKey: string, provider: string) => {
    setSavingKey(serviceKey);
    try {
      await apiClient.put('/ai-tools/service-provider', { service_key: serviceKey, provider });
      setRows((prev) => prev.map((r) => r.service_key === serviceKey ? { ...r, provider } : r));
      toast({ title: 'Provider aggiornato', description: serviceKey + ' -> ' + provider });
    } catch (err: unknown) {
      // Task #2016 — preferenza al payload strutturato `{ error: { code,
      // message_it, hint? } }` esposto da /ai-tools/service-provider (es.
      // EMBEDDER_LOCKED, COHERENCE_VIOLATION, PROVIDER_UNAVAILABLE).
      const structured = extractStructuredApiError(err);
      const title = structured?.code
        ? `Errore provider (${structured.code})`
        : 'Errore provider';
      const description = structured
        ? (structured.hint ? `${structured.message_it} — ${structured.hint}` : structured.message_it)
        : (err instanceof Error ? err.message : String(err));
      toast({ title, description, variant: 'destructive' });
    } finally {
      setSavingKey(null);
    }
  }, [toast]);

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-3">
        <Loader2 className="h-3 w-3 animate-spin" />
        Caricamento altri servizi...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-xs text-amber-700 py-3">
        Errore caricamento: {error}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-xs text-gray-500 py-3">
        Tutti i service_key di platform_model_config sono gia' coperti sopra.
      </div>
    );
  }

  return (
    <div className="space-y-2 py-2" data-testid="other-platform-services">
      <p className="text-[11px] text-gray-500 mb-2">
        {rows.length} servizi piattaforma aggiuntivi. Provider e modello sono
        validati contro <code>ai_model_catalog</code>.
      </p>
      {rows.map((row) => {
        const meta: ServiceMeta = SERVICE_META[row.service_key] ?? { label: row.service_key, tooltip: '', usedIn: '' };
        return (
        <div
          key={row.service_key}
          className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 rounded-md border border-gray-200 dark:border-gray-700 bg-white/60 dark:bg-gray-900/40"
          data-testid={`other-service-row-${row.service_key}`}
        >
          <div className="min-w-[14rem] flex-1 flex flex-col gap-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-800 dark:text-gray-100">{meta.label}</span>
            </div>
            {meta.tooltip && (
              <FunctionHelp
                testId={`service-details-${row.service_key}`}
                content={{ whatItDoes: meta.tooltip, whenItRuns: meta.usedIn || undefined }}
              />
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 max-w-full">
            <ServiceProviderSelect
              serviceKey={row.service_key}
              currentProvider={row.provider}
              availability={providerAvailabilityByKey[row.service_key] ?? { status: 'loading' }}
              onChange={(p) => handleProviderChange(row.service_key, p)}
              saving={savingKey === row.service_key}
              widthClass="w-[140px]"
            />
            <ServiceModelPicker
              platformServiceKey={row.service_key}
              provider={row.provider}
              widthPx={260}
              platformConfigEndpoint="/ai-tools/service-models/"
              buildSaveEndpoint={(k) => `/ai-tools/service-models/${k}`}
              readOnly={isServicePlatformScoped(row.service_key)}
              readOnlyReason={PLATFORM_SCOPED_READONLY_REASON}
            />
          </div>
        </div>
        );
      })}
    </div>
  );
}
